import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/common/empty-state';
import {
  ProductCard,
  ProductCardSkeleton,
  CategoryPills,
  SearchFilterBar,
} from '@/features/mercado/components';
import { useListings } from '@/features/mercado/api';
import { useCondoContext } from '@/features/auth/use-condo-context';
import { MOCK_TENANT_ID } from '@/lib/auth';
import { PageContainer } from '@/components/common/page-container';
import { PageHeader } from '@/components/common/page-header';
import { AdminMercadoFeed } from './admin-feed.js';
import type { Listing, Rol } from '@condo/contracts';

const MANAGEMENT_ROLES: Rol[] = ['super_admin', 'admin', 'comite'];

type CategoryId =
  | 'todos'
  | 'muebles'
  | 'electronica'
  | 'electrodomesticos'
  | 'ropa'
  | 'deportes'
  | 'hogar'
  | 'libros'
  | 'juguetes'
  | 'vehiculos'
  | 'otros';

interface Filters {
  query: string;
  priceMin?: number;
  priceMax?: number;
  conditions: Array<'nuevo' | 'como_nuevo' | 'buen_estado' | 'usado'>;
  sameBuildingOnly: boolean;
  sort: 'reciente' | 'precio_asc' | 'precio_desc' | 'popular' | 'cercano';
}

const DEFAULT_FILTERS: Filters = {
  query: '',
  conditions: [],
  sameBuildingOnly: false,
  sort: 'reciente',
};

function applyClientFilters(
  listings: Listing[],
  filters: Filters,
  userTenantId: string,
): Listing[] {
  let items = [...listings];

  // Text search (client-side, simple includes)
  if (filters.query.trim()) {
    const q = filters.query.toLowerCase();
    items = items.filter(
      (l) =>
        l.title.toLowerCase().includes(q) ||
        (l.description?.toLowerCase().includes(q) ?? false),
    );
  }

  // Price range
  if (filters.priceMin !== undefined) {
    items = items.filter((l) => l.priceCLP >= filters.priceMin!);
  }
  if (filters.priceMax !== undefined) {
    items = items.filter((l) => l.priceCLP <= filters.priceMax!);
  }

  // Condition
  if (filters.conditions.length > 0) {
    items = items.filter((l) => filters.conditions.includes(l.condition));
  }

  // Same building only
  if (filters.sameBuildingOnly) {
    items = items.filter((l) => l.sellerTenantId === userTenantId);
  }

  // Sort
  switch (filters.sort) {
    case 'precio_asc':
      items.sort((a, b) => a.priceCLP - b.priceCLP);
      break;
    case 'precio_desc':
      items.sort((a, b) => b.priceCLP - a.priceCLP);
      break;
    case 'reciente':
    default:
      items.sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      );
      break;
  }

  return items;
}

function ResidentMercadoFeed() {
  const navigate = useNavigate();
  const { condoId: tenantId } = useCondoContext();
  const effectiveTenantId = tenantId || MOCK_TENANT_ID;

  const [category, setCategory] = React.useState<CategoryId>('todos');
  const [filters, setFilters] = React.useState<Filters>(DEFAULT_FILTERS);

  const { data, isLoading } = useListings({
    category: category !== 'todos' ? category : undefined,
  });

  const listings = data?.items ?? [];
  const filtered = applyClientFilters(listings, filters, effectiveTenantId);

  // Compute category counts from unfiltered data
  const allListings = useListings();
  const categoryCounts = React.useMemo(() => {
    const all = allListings.data?.items ?? [];
    const counts: Partial<Record<CategoryId, number>> = { todos: all.length };
    for (const l of all) {
      counts[l.category as CategoryId] = (counts[l.category as CategoryId] ?? 0) + 1;
    }
    return counts;
  }, [allListings.data]);

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Comunidad"
        title="Mercado Vecinal"
        subtitle="Compra y vende entre vecinos de tu comunidad."
        action={
          <Button
            variant="accent"
            onClick={() => navigate(`/app/condominios/${tenantId}/mercado/publicar`)}
          >
            <Plus className="h-4 w-4" />
            Vender
          </Button>
        }
      />

      {/* Category pills */}
      <CategoryPills
        selected={category}
        onSelect={(id) => setCategory(id as CategoryId)}
        counts={categoryCounts}
        className="mb-4"
      />

      {/* Search + Filters */}
      <SearchFilterBar
        filters={filters}
        onFiltersChange={setFilters}
        resultCount={filtered.length}
        className="mb-6"
      />

      {/* Grid */}
      {isLoading ? (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <ProductCardSkeleton key={i} />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          title="Sin resultados"
          description="No hay articulos que coincidan con tu busqueda. Intenta con otros filtros."
        />
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {filtered.map((listing) => (
            <ProductCard
              key={listing.id}
              id={listing.id}
              imageUrl={listing.photos[0]?.url}
              title={listing.title}
              priceCLP={listing.priceCLP}
              sellerName={listing.sellerName}
              sellerRating={4.5}
              condition={listing.condition}
              createdAt={listing.createdAt}
              isSameBuilding={listing.sellerTenantId === effectiveTenantId}
              condominiumName={listing.condominiumName}
              onClick={() => navigate(`/app/condominios/${tenantId}/mercado/${listing.id}`)}
            />
          ))}
        </div>
      )}
    </PageContainer>
  );
}

export function MercadoFeedRoute() {
  const { rol } = useCondoContext();
  const isManager = rol !== undefined && MANAGEMENT_ROLES.includes(rol);
  return isManager ? <AdminMercadoFeed /> : <ResidentMercadoFeed />;
}
